"use client";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Button, Container, Stack } from "@mui/material";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { switchChain } from "@wagmi/core";
import { MAINNET_BRIDGE, configs, promMainnet } from "config/default";
import { type ChainId, wagmiConfig } from "config/wagmi";
import { DepositsToClaim } from "modules/bridge/DepositsToClaim";
import { History } from "modules/bridge/History";
import { useBridges } from "modules/bridge/api/useBridges";
import { Warning } from "modules/bridge/assets/Warning";
import { ActionButton } from "modules/bridge/components/ActionButton";
import { BridgeInfo } from "modules/bridge/components/BridgeInfo";
import { BridgeProcessModal } from "modules/bridge/components/BridgeProcessModal";
import { NetworkSelect } from "modules/bridge/components/NetworkSelect";
import { BridgeTabs } from "modules/bridge/components/Tabs";
import { TokenAmountInput } from "modules/bridge/components/TokenAmountInput";
import { TokenDialog } from "modules/bridge/components/TokenDialog";
import {
  bridgeNetworksConfig,
  bridgeTokenConfig,
  chainNamesById,
} from "modules/bridge/config";
import { useBridge } from "modules/bridge/hooks/useBridge";
import { useERC20Allowance } from "modules/bridge/hooks/useERC20Allowance";
import { useERC20Approve } from "modules/bridge/hooks/useERC20Approve/useERC20Approve";
import { useWarpCore } from "modules/bridge/hyperlane/features/tokens/hooks";
import { useTokenTransfer } from "modules/bridge/hyperlane/features/transfer/useTokenTransfer";
import type { FormValues } from "modules/bridge/types";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { type Address, parseEther, parseUnits } from "viem";
import { useAccount, useBalance } from "wagmi";
import { bsc } from "wagmi/chains";
import Background from "./assets/img.png";

const [BSC, prom] = bridgeNetworksConfig;

const ERROR_LIQUIDITY_BRIDGE =
  "The bridge does not have sufficient liquidity of this token to complete the transfer.";

export function Bridge() {
  const [selectedToken, setSelectedToken] = useState<{
    address: Address;
    chainId: ChainId;
  }>();
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState("deposit");
  const [bridgeProcessModalOpen, setBridgeProcessModalOpen] = useState(false);

  const form = useForm<FormValues>({
    mode: "onChange",
    defaultValues: { chainIdFrom: BSC.chainId, chainIdTo: prom.chainId },
  });
  const {
    watch,
    formState: { isValid },
  } = form;
  const values = watch();
  const warpCore = useWarpCore();
  const isEth =
    selectedToken?.address === "0x0000000000000000000000000000000000000000";

  const tokenListByChainIdList = values.chainIdFrom
    ? bridgeTokenConfig[values.chainIdFrom as ChainId].filter((token) =>
        token?.networksTo.length
          ? token.networksTo.includes(+values.chainIdTo)
          : values.chainIdTo === 1 || values.chainIdTo === 227
      )
    : [];

  const selectedTokenData = tokenListByChainIdList.find(
    (token) =>
      token.address === selectedToken?.address &&
      token.chainId === selectedToken?.chainId &&
      (token.networksTo?.length
        ? token.networksTo.includes(values.chainIdTo as ChainId)
        : values.chainIdTo === 1 || values.chainIdTo === 227)
  );

  const tokenToData = bridgeTokenConfig[values.chainIdTo as ChainId].find(
    ({ symbol }) => symbol === selectedTokenData?.symbol
  );

  const { data: bridgeTokenBalance, error: balanceError } = useBalance({
    address: tokenToData?.routerAddress as Address,
    token: tokenToData?.address as Address,
    chainId: values.chainIdTo as ChainId,
    scopeKey: `balance-liquidity-${tokenToData?.routerAddress}-${tokenToData?.address}-${values.chainIdFrom}-${values.chainIdTo}`,
    query: {
      enabled: !!(
        tokenToData?.routerAddress &&
        tokenToData?.address &&
        values.chainIdTo
      ),
    },
  });

  const getNetworkToBalanceError = (): string => {
    if (!bridgeTokenBalance) {
      return "";
    }

    const isInsufficientBalance =
      +(bridgeTokenBalance?.formatted || 0) < +values.amount;
    if (
      values.chainIdFrom === bsc.id &&
      values.chainIdTo === promMainnet.id &&
      selectedTokenData?.symbol === "PROM" &&
      isInsufficientBalance
    ) {
      return ERROR_LIQUIDITY_BRIDGE;
    }

    if (values.chainIdTo !== promMainnet.id && isInsufficientBalance) {
      return ERROR_LIQUIDITY_BRIDGE;
    }
    return "";
  };

  const networkToBalanceError = getNetworkToBalanceError();
  const { data: depositList, isLoading: isLoadingDepositList } = useBridges();

  const parsedAmount = isEth
    ? parseEther(values.amount || "0")
    : parseUnits(values.amount || "0", selectedTokenData?.decimals ?? 0);

  const bridgeContractAddress = configs[
    values.chainIdFrom as keyof typeof configs
  ]?.contracts?.bridge as Address;

  const { address: account } = useAccount();
  const fee = 0;
  const balance = useBalance({
    address: account,
    chainId: values.chainIdFrom as ChainId,
    token: isEth ? undefined : (selectedToken?.address as Address),
  });

  const nativeToken = bridgeNetworksConfig.find(
    (it) => it.chainId === values.chainIdFrom
  )?.nativeToken;

  const isDeposit = currentTab === "deposit";

  const connect = useConnectModal();
  const { address } = useAccount();
  const handleAddNetwork = () => {
    if (!address) {
      connect?.openConnectModal?.();
    }
    switchChain(wagmiConfig, { chainId: 227 });
  };

  const { triggerTransactions } = useTokenTransfer();

  const bridgeRouter =
    (selectedTokenData?.routerAddress as Address) || MAINNET_BRIDGE;

  const { isAllowed, isLoading: isAllowanceLoading } = useERC20Allowance({
    amount: parsedAmount,
    token: selectedToken?.address as Address,
    spender: bridgeRouter,
    disabled: isEth,
  });

  const [isError, setError] = useState<Error | null>(null);
  const [isHyperlaneSuccess, setIsHyperlaneSuccess] = useState(false);

  const tokenIndex = warpCore.tokens.findIndex((t) =>
    t.collateralAddressOrDenom
      ? t.collateralAddressOrDenom === selectedTokenData?.address &&
        t.addressOrDenom === selectedTokenData?.routerAddress
      : t.addressOrDenom === selectedTokenData?.routerAddress
  );
  const token = warpCore.tokens[tokenIndex];

  const {
    bridgeAsset,
    error,
    isSuccess,
    isPending,
    isConfirming,
    resetApprove,
    reset,
  } = useBridge({
    amount: parsedAmount,
    destinationAddress: account as Address,
    from: {
      chainId: values.chainIdFrom as ChainId,
      bridgeContractAddress,
    },
    to: {
      networkId: values.chainIdTo as ChainId,
      bridgeContractAddress: configs[values.chainIdTo as keyof typeof configs]
        ?.contracts?.bridge as Address,
    },
    token: selectedToken?.address as Address,
    // permitData: await getPermitData(),
  });

  const handleAction = () => {
    reset();
    resetApprove();
    setIsHyperlaneSuccess(false);
    setError(null);
  };

  const handleToggleTokenModal = () => {
    setTokenModalOpen((prev) => !prev);
  };

  const handleToggleBridgeProcessModal = () => {
    setBridgeProcessModalOpen((prev) => !prev);
    reset();
    resetApprove();
    setIsHyperlaneSuccess(false);
    setError(null);
  };

  const handleSetCurrentTab = (tab: string) => {
    setCurrentTab(tab);
    // setValue("chainIdFrom", getOppositeChain(values.chainIdFrom));
    // setValue("chainIdTo", getOppositeChain(values.chainIdTo));
  };

  const handleTokenSelect = (address: Address, chainId: number) => {
    setSelectedToken({ address, chainId });
    setTokenModalOpen(false);
  };

  useEffect(() => {
    if (values.chainIdFrom) {
      setSelectedToken({
        chainId: values.chainIdFrom as ChainId,
        address: tokenListByChainIdList?.[0]?.address as Address,
      });
      reset();
    }
  }, [values.chainIdFrom]);

  useEffect(() => {
    if (values.chainIdTo) {
      setSelectedToken({
        chainId: values.chainIdFrom as ChainId,
        address: tokenListByChainIdList?.[0]?.address as Address,
      });
      reset();
    }
  }, [values.chainIdTo]);
  const isInsufficientBalance =
    parseFloat(values.amount) > parseFloat(balance?.data?.formatted || "0");

  const getButtonText = () => {
    if (isInsufficientBalance) {
      return "Insufficient balance";
    }
    if (isApproving && !isApproveSuccess) {
      return "Approving...";
    }

    if (!isEth && isAllowanceLoading) {
      return "Fetching data...";
    }

    if (isAllowed || receipt || isEth) {
      return "Bridge";
    }

    return "Approve";
  };

  const {
    approve,
    state: {
      isPending: isApproving,
      error: erc20ApproveError,
      isSuccess: isApproveSuccess,
    },
    receipt,
    receiptState: { isLoading: isApproveLoadingReceipt },
  } = useERC20Approve();

  if (erc20ApproveError) {
    console.error(erc20ApproveError);
  }

  const handleHyperlane = async () => {
    try {
      if (networkToBalanceError) {
        setTokenModalOpen(true);
        return;
      }

      if (selectedTokenData) {
        await triggerTransactions({
          origin: token.chainName,
          destination:
            chainNamesById[values.chainIdTo as keyof typeof chainNamesById],
          tokenIndex: tokenIndex,
          amount: values.amount,
          recipient: account as string,
        });
        setIsHyperlaneSuccess(true);
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(e);
      } else {
        setError(new Error("An error occurred"));
      }
    }
  };

  const handleBridge = async () => {
    setError(null);
    setBridgeProcessModalOpen(true);

    if (selectedTokenData?.routerAddress) {
      handleHyperlane();
    } else {
      if (isEth || isAllowed || receipt) {
        await bridgeAsset();
      }
    }
  };

  const handleApprove = () => {
    approve({
      tokenAddress: selectedToken?.address.toLowerCase() as Address,
      spender: bridgeRouter,
      amount: parsedAmount,
    });
  };

  return (
    <>
      <img
        src={Background.src}
        alt="background"
        width="100%"
        style={{ marginBottom: "60px" }}
      />
      <DepositsToClaim list={depositList?.deposits} />
      <Container
        maxWidth="sm"
        style={{
          maxWidth: "640px",
          padding: 0,
        }}
      >
        <Stack
          sx={{
            backgroundColor: "secondary.main",
            padding: { xs: 3, md: 8 },
            alignItems: "center",
            borderRadius: 4,
          }}
          spacing={12}
        >
          <BridgeTabs
            currentTab={currentTab}
            onSetCurrentTab={handleSetCurrentTab}
          />

          {isDeposit ? (
            <FormProvider {...form}>
              <Stack spacing={4} sx={{ width: "100%" }}>
                <NetworkSelect label="From" name="chainIdFrom" />
                <NetworkSelect label="To" name="chainIdTo" />
              </Stack>
              <Stack
                direction="row"
                width="100%"
                justifyContent="space-between"
                alignItems="end"
                gap={1}
              >
                <TokenDialog
                  open={tokenModalOpen}
                  onToggleTokenModal={handleToggleTokenModal}
                  onTokenSelect={handleTokenSelect}
                  logoURI={selectedTokenData?.logoURI}
                  name={selectedTokenData?.name}
                  symbol={selectedTokenData?.symbol}
                />
                <TokenAmountInput
                  name="amount"
                  symbol={selectedTokenData?.symbol}
                  balance={balance?.data?.formatted || "0"}
                />
              </Stack>
              <KeyboardArrowDownIcon />
              <BridgeInfo
                isHyperLine={!!selectedTokenData?.routerAddress}
                amount={values.amount}
                fee={fee}
                nativeToken={nativeToken}
                chainIdFrom={values?.chainIdFrom as ChainId}
                chainIdTo={values?.chainIdTo as ChainId}
                symbol={selectedTokenData?.symbol}
                origin={token?.chainName}
                destination={
                  chainNamesById?.[
                    values.chainIdTo as keyof typeof chainNamesById
                  ]
                }
                tokenIndex={tokenIndex}
                recipient={account}
              />
              <Stack gap={4} width="100%">
                <Button
                  onClick={handleAddNetwork}
                  variant="outlined"
                  color="secondary"
                  fullWidth
                >
                  Add Prom Network to Your Wallet
                </Button>
                <ActionButton
                  isLoading={
                    (!isEth && isAllowanceLoading) ||
                    isApproving ||
                    isApproveLoadingReceipt ||
                    isPending ||
                    isConfirming
                  }
                  buttonText={getButtonText()}
                  onBridge={isAllowed || isEth ? handleBridge : handleApprove}
                  chainIdFrom={values.chainIdFrom as ChainId}
                  isValid={
                    isValid &&
                    values.chainIdFrom !== values.chainIdTo &&
                    !isInsufficientBalance
                  }
                />
                <Warning />
              </Stack>
            </FormProvider>
          ) : (
            <History
              list={depositList?.deposits}
              isLoading={isLoadingDepositList}
            />
          )}
        </Stack>
      </Container>
      <BridgeProcessModal
        type={
          networkToBalanceError || error || isError
            ? "failed"
            : isPending
            ? "waiting"
            : isSuccess || isHyperlaneSuccess
            ? "confirmed"
            : "waiting"
        }
        errorMessage={
          networkToBalanceError || error?.message || isError?.message
        }
        open={bridgeProcessModalOpen}
        onClose={handleToggleBridgeProcessModal}
        onAction={handleAction}
      />
    </>
  );
}
